import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

const FallbackContainer = styled.div.attrs({ className: 'fallback-starmap-container' })`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* Force perfect square aspect ratio */
  background-color: ${props => props.background || '#000'};
  overflow: hidden;
  border-radius: 50%;
`;

const SVGContainer = styled.svg.attrs({ className: 'fallback-starmap-svg' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  aspect-ratio: 1; /* Force a 1:1 aspect ratio */
`;

const StarPoint = styled.circle.attrs({ className: 'fallback-star-point' })`
  fill: #fff;
  opacity: ${props => props.brightness || 0.8};
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const ConstellationLine = styled.line.attrs({ className: 'fallback-constellation-line' })`
  stroke: rgba(255, 255, 255, 0.3);
  stroke-width: 1;
`;

const ConstellationName = styled.text.attrs({ className: 'fallback-constellation-name' })`
  fill: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 300;
  text-anchor: middle;
  font-family: 'Montserrat', sans-serif;
`;

const LocationInfo = styled.text.attrs({ className: 'fallback-location-info' })`
  fill: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  text-anchor: middle;
  font-family: 'Montserrat', sans-serif;
  dominant-baseline: hanging;
`;

const DateInfo = styled.text.attrs({ className: 'fallback-date-info' })`
  fill: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  text-anchor: middle;
  font-family: 'Montserrat', sans-serif;
  dominant-baseline: hanging;
`;

const CaptionText = styled.text.attrs({ className: 'fallback-caption-text' })`
  fill: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 600;
  text-anchor: middle;
  font-family: 'Montserrat', sans-serif;
  dominant-baseline: hanging;
`;

const generateRandomStar = (width, height) => {
    const brightness = Math.random();
    const size = brightness * 2 + 0.5; // Sizes between 0.5 and 2.5

    return {
        x: Math.random() * width,
        y: Math.random() * height,
        size,
        opacity: brightness * 0.9 + 0.1 // Opacity between 0.1 and 1
    };
};

const generateRandomConstellation = (stars, width, height) => {
    const constellationSize = Math.floor(Math.random() * 6) + 3; // Between 3 and 8 stars
    const centerX = Math.random() * width;
    const centerY = Math.random() * height;
    const radius = Math.min(width, height) * (Math.random() * 0.15 + 0.05); // Smaller constellations

    // Select random stars near the center point
    const constellationStars = Array.from({ length: constellationSize }, () => {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * radius;

        return {
            x: centerX + Math.cos(angle) * distance,
            y: centerY + Math.sin(angle) * distance,
            size: Math.random() * 1.5 + 1.0,
            opacity: Math.random() * 0.5 + 0.5
        };
    });

    // Create lines between stars (not all stars, to create a pattern)
    const lines = [];
    for (let i = 0; i < constellationStars.length - 1; i++) {
        if (Math.random() > 0.3) { // 70% chance to create a line
            lines.push({
                x1: constellationStars[i].x,
                y1: constellationStars[i].y,
                x2: constellationStars[i + 1].x,
                y2: constellationStars[i + 1].y
            });
        }
    }

    // Create a random name
    const constellationNames = [
        "Orion", "Lyra", "Ursa", "Cygnus", "Gemini", "Draco",
        "Cassiopeia", "Perseus", "Andromeda", "Pegasus", "Phoenix",
        "Cepheus", "Hydra", "Hercules", "Aquila", "Delphinus"
    ];

    const nameSuffix = ["Minor", "Major", "Australis", "Borealis", "Omega", "Prime"];
    let name = constellationNames[Math.floor(Math.random() * constellationNames.length)];

    // 30% chance to add a suffix
    if (Math.random() < 0.3) {
        name += " " + nameSuffix[Math.floor(Math.random() * nameSuffix.length)];
    }

    return {
        stars: constellationStars,
        lines,
        name,
        nameX: centerX,
        nameY: centerY + radius + 15 // Position name below constellation
    };
};

const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (e) {
        return '';
    }
};

const formatLocation = (location) => {
    if (!location) return '';

    try {
        if (typeof location === 'string') {
            // Handle string format like "lat,lon"
            const [lat, lon] = location.split(',').map(n => parseFloat(n.trim()));
            return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;
        } else if (location.latitude !== undefined && location.longitude !== undefined) {
            // Handle object format {latitude, longitude}
            const lat = parseFloat(location.latitude);
            const lon = parseFloat(location.longitude);
            return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;
        }
        return '';
    } catch (e) {
        return '';
    }
};

const FallbackStarMap = ({ date, location, caption, background, style = {} }) => {
    const [stars, setStars] = useState([]);
    const [constellations, setConstellations] = useState([]);
    const [dimensions, setDimensions] = useState({ width: 600, height: 600 });
    const containerRef = useRef(null);

    const formattedDate = formatDate(date);
    const formattedLocation = formatLocation(location);

    // Get background color based on background type
    const getBackgroundColor = (backgroundType) => {
        if (!backgroundType) return '#000';

        const backgrounds = {
            'gray': '#808080', // Fallback - PNG is used in actual generation
            'black': '#000000', // Fallback - PNG is used in actual generation
            'basic-light': '#f0f0f5',
            'basic-dark': '#141420',
            'sunset-pink': '#2D1B3D',
            'sunset-orange': '#3D2415',
            'golden-black': '#1A1611',
            'neon-blue': '#0B1A2D',
            'pastel-pink': '#3D2B3A',
            'pastel-blue': '#2B3A3D',
            'pastel-white': '#F5F0F0',
            'neon-purple': '#2B1A3D',
            'natural-gold': '#3D3420',
            'natural-brown': '#3D2B20',
            'natural-beige': '#3D3B2B',
            'golden-dark': '#3D2D1E',
            'golden-gold': '#3D3220'
        };

        return backgrounds[backgroundType] || '#000000';
    };

    const backgroundColor = getBackgroundColor(background);

    useEffect(() => {
        if (!containerRef.current) return;

        const generateStarMap = () => {
            const element = containerRef.current;
            const width = element.clientWidth || 600;
            // Force height to match width for a perfect square
            const height = width;

            setDimensions({ width, height });

            // Generate random stars within the circular area only
            const randomStars = Array.from({ length: 800 }, () => {
                const star = generateRandomStar(width, height);
                // Only keep stars that fall within the circle
                const centerX = width / 2;
                const centerY = height / 2;
                const radius = width / 2;

                // Check if the star is inside the circle
                const distance = Math.sqrt(
                    Math.pow(star.x - centerX, 2) +
                    Math.pow(star.y - centerY, 2)
                );

                if (distance <= radius) {
                    return star;
                }

                // If outside the circle, regenerate at a random position inside the circle
                const angle = Math.random() * Math.PI * 2;
                const r = Math.sqrt(Math.random()) * radius; // Square root for uniform distribution

                return {
                    ...star,
                    x: centerX + r * Math.cos(angle),
                    y: centerY + r * Math.sin(angle)
                };
            });

            // Generate 3-5 constellations within the circle
            const numberOfConstellations = Math.floor(Math.random() * 3) + 3;
            const randomConstellations = Array.from(
                { length: numberOfConstellations },
                () => {
                    // Ensure constellations are within the circle
                    const constellation = generateRandomConstellation(randomStars, width, height);

                    // Adjust the center to be within 80% of the radius to avoid edge clipping
                    const centerX = width / 2;
                    const centerY = height / 2;
                    const radius = width * 0.4; // 80% of radius to keep away from edges

                    const angle = Math.random() * Math.PI * 2;
                    const distance = Math.random() * radius;

                    // Update constellation position
                    const newX = centerX + Math.cos(angle) * distance;
                    const newY = centerY + Math.sin(angle) * distance;

                    // Offset all stars and lines by the difference
                    const offsetX = newX - constellation.nameX;
                    const offsetY = newY - constellation.nameY;

                    constellation.nameX = newX;
                    constellation.nameY = newY;

                    constellation.stars = constellation.stars.map(star => ({
                        ...star,
                        x: star.x + offsetX,
                        y: star.y + offsetY
                    }));

                    constellation.lines = constellation.lines.map(line => ({
                        ...line,
                        x1: line.x1 + offsetX,
                        y1: line.y1 + offsetY,
                        x2: line.x2 + offsetX,
                        y2: line.y2 + offsetY
                    }));

                    return constellation;
                }
            );

            setStars(randomStars);
            setConstellations(randomConstellations);
        };

        generateStarMap();

        // Add resize handler
        const handleResize = () => {
            generateStarMap();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Add subtle twinkling animation to stars
    useEffect(() => {
        const twinkleInterval = setInterval(() => {
            setStars(prevStars =>
                prevStars.map(star => ({
                    ...star,
                    opacity: Math.max(0.1, Math.min(1, star.opacity + (Math.random() * 0.1 - 0.05)))
                }))
            );
        }, 1000);

        return () => clearInterval(twinkleInterval);
    }, []);

    return (
        <FallbackContainer ref={containerRef} style={style} background={backgroundColor}>
            <SVGContainer>
                {/* Random Background Stars */}
                {stars.map((star, index) => (
                    <StarPoint
                        key={`star-${index}`}
                        cx={star.x}
                        cy={star.y}
                        r={star.size}
                        opacity={star.opacity}
                    />
                ))}

                {/* Constellations */}
                {constellations.map((constellation, constellationIndex) => (
                    <React.Fragment key={`constellation-${constellationIndex}`}>
                        {/* Constellation Lines */}
                        {constellation.lines.map((line, lineIndex) => (
                            <ConstellationLine
                                key={`constellation-${constellationIndex}-line-${lineIndex}`}
                                x1={line.x1}
                                y1={line.y1}
                                x2={line.x2}
                                y2={line.y2}
                            />
                        ))}

                        {/* Constellation Stars */}
                        {constellation.stars.map((star, starIndex) => (
                            <StarPoint
                                key={`constellation-${constellationIndex}-star-${starIndex}`}
                                cx={star.x}
                                cy={star.y}
                                r={star.size}
                                opacity={star.opacity}
                            />
                        ))}

                        {/* Constellation Name */}
                        <ConstellationName
                            x={constellation.nameX}
                            y={constellation.nameY}
                        >
                            {constellation.name}
                        </ConstellationName>
                    </React.Fragment>
                ))}

                {/* Display date and location information at the bottom */}
                {formattedLocation && (
                    <LocationInfo x={dimensions.width / 2} y={dimensions.height - 60}>
                        {formattedLocation}
                    </LocationInfo>
                )}

                {formattedDate && (
                    <DateInfo x={dimensions.width / 2} y={dimensions.height - 40}>
                        {formattedDate}
                    </DateInfo>
                )}

                {caption && (
                    <CaptionText x={dimensions.width / 2} y={dimensions.height - 90}>
                        {caption}
                    </CaptionText>
                )}
            </SVGContainer>
        </FallbackContainer>
    );
};

export default FallbackStarMap; 